import {
  Button,
  CustomDtcBagButton,
  MOBILE_SCREEN_SIZE,
  StoreBrandingType,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { ClickAwayListener, Popper, styled, useTheme } from "@mui/material";
import React, { useState } from "react";

import { getCartCounts } from "@/redux/selectors";
import HamburgerMenuButton from "../pages/AppPages/Dashboard/DashboardBottomBar/HamburgerMenuButton";
import { useAppSelector } from "@/redux/hooks";
import { useNavigate } from "react-router-dom";
import { scaleFont } from "@/utils/font";
import { usePrefetchDTCOffers } from "../pages/AppPages/Dashboard/Offers/usePrefetchDTCOffers";
import useStoreType from "@/utils/hooks/useStoreType";

const BarOutlineButton = styled(Button)(({ theme }) => ({
  fontSize: scaleFont(theme.typography.fontSize, 16),
  fontWeight: 500,
  borderWidth: 1,
  width: "fit-content",
  height: 52,
  padding: "0px 20px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    height: 46,
    fontSize: scaleFont(theme.typography.fontSize, 14),
  },
}));

const menuItems = [
  {
    label: "Dashboard",
    to: "/me",
  },
  {
    label: "Purchases",
    to: "/purchase",
  },
  {
    label: "Accounts",
    to: "/accounts",
  },
  {
    label: "Rewards",
    to: "/rewards/balance",
  },
  {
    label: "Offers",
    to: "/offers",
  },
  {
    label: "Newsletter",
    to: "/newsletter",
  },

  {
    label: "Personal",
    to: "/profile",
  },
];

const HeaderButtonWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  position: "absolute",
  right: "21%",
  [`@media (max-width: 1350px)`]: {
    right: "5%",
  },
}));

const HeaderBagButtonTextContent = styled("span")(({ theme }) => ({
  fontSize: 18,
  fontWeight: 700,
  color: theme.vars.palette.text.primary,
  transform: theme?.settings?.fonts?.fontOffsetY
    ? `translateY(${theme?.settings?.fonts?.fontOffsetY}%)`
    : undefined,
}));

const BagButton = styled(Button)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    display: "none",
  },
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    display: "none",
  },
}));

export const HeaderBagButton = () => {
  const theme = useTheme();
  const inPageCheckout = theme?.dtcMode === "inpage";
  const cartCount = useAppSelector(getCartCounts);
  const content = theme.content?.cartButton?.signedInDesktop;
  const customImage = content?.customIconStartSrc ?? content?.customIconEndSrc;
  const textButton = !customImage;
  const navigate = useNavigate();

  const handleViewBag = () => {
    navigate("/mybag");
  };

  if (!inPageCheckout || !cartCount) {
    return null;
  }
  return (
    <HeaderButtonWrapper
      sx={{ flex: inPageCheckout ? undefined : "none" }}
      onClick={handleViewBag}
    >
      <BagButton variant="text" color="default">
        <HeaderBagButtonTextContent>
          {textButton && !!cartCount ? `View bag (${cartCount})` : "View bag"}
        </HeaderBagButtonTextContent>
      </BagButton>

      {!textButton && (
        <CustomDtcBagButton handleClick={handleViewBag} cartCount={cartCount} />
      )}
    </HeaderButtonWrapper>
  );
};

const HamburgerComponent = styled("div")<{ inPageCheckout: boolean }>(
  ({ inPageCheckout }) => ({
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "none",
    gap: "8px",
    position: "absolute",
    left: "21%",
    display: "none",
    [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      display: inPageCheckout ? "flex" : "none",
      left: "5%",
    },
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      display: inPageCheckout ? "flex" : "none",
      left: "5%",
    },
  })
);

const Dropdown = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  padding: "12px 8px",
  boxSizing: "border-box",
  border: "1px solid #dcdddc80",
  backgroundColor: " #ffffff",
  shadow: "0 3px 15px 0 rgba(195, 188, 188, 0.15)",
  borderRadius: "8px",
});

export const HeaderMenuResponsiveMenu = () => {
  const theme = useTheme();
  const prefetchAllDTCOffers = usePrefetchDTCOffers();
  const inPageCheckout = theme?.dtcMode === "inpage";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const storeType = useStoreType();
  const isDtc = storeType === StoreBrandingType.CustomDTCStore;

  const brandAppConfig = useAppSelector(
    (state) => state.appConfig?.brandAppConfig
  );
  const enableRewards = isDtc
    ? brandAppConfig?.modulesConfig?.rewardsModule?.enabled
    : true;
  const enableOffers = isDtc
    ? brandAppConfig?.modulesConfig?.offersModule?.enabled
    : true;
  const enableNewsletter = isDtc
    ? brandAppConfig?.modulesConfig?.newsletterModule?.enabled
    : false;

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.label === "Rewards" && !enableRewards) {
      return false;
    }
    if (item.label === "Offers" && !enableOffers) {
      return false;
    }
    if (item.label === "Newsletter" && !enableNewsletter) {
      return false;
    }
    return true;
  });

  const navigate = useNavigate();
  const handleToggleMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (!anchorEl) {
      // if anchorEl is null, we are opening the menu, so we prefetch all DTC offers
      prefetchAllDTCOffers();
    }
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? "menu-popper" : undefined;

  return (
    <>
      <HamburgerComponent
        inPageCheckout={inPageCheckout}
        onClick={handleToggleMenu}
        aria-describedby={id}
        role="button"
      >
        <HamburgerMenuButton width={22.4} height={18} />
      </HamburgerComponent>
      <Popper
        placement="bottom-start"
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 28],
            },
          },
        ]}
        className="menu-popper"
        sx={{
          zIndex: 1,
        }}
        id={id}
        open={open}
        anchorEl={anchorEl}
      >
        <ClickAwayListener onClickAway={handleToggleMenu as any}>
          <Dropdown>
            {filteredMenuItems.map((item) => {
              return (
                <BarOutlineButton
                  color="default"
                  variant="outline"
                  key={item.label}
                  onClick={(e) => {
                    navigate(item.to);
                    handleToggleMenu(e);
                  }}
                >
                  {item.label}
                </BarOutlineButton>
              );
            })}
          </Dropdown>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
